import React, { useState, useEffect, useMemo } from 'react';
import { saveAs } from 'file-saver';
import Explain from './components/Explain';
import Show from './components/Show';
import Summary from './components/Summary';
import ProblemPage from './components/ProblemPage';
import FinishPage from './components/FinishPage';
import CalculationPage from './components/CalculationPage';

// 固定されたストーリーと対応する問題
const fixedStory = require('./data/story/story7.json');
const fixedProblem = require('./data/problem/problem7.json');

// 可変ストーリーと対応する問題
const variableStories = [
  require('./data/story/story0.json'),
  require('./data/story/story1.json'),
  require('./data/story/story2.json'),
];
const variableProblems = [
  require('./data/problem/problem0.json'),
  require('./data/problem/problem1.json'),
  require('./data/problem/problem2.json'),
];

const storyGroups = [
  [fixedStory, variableStories[0], variableStories[1], variableStories[2]],
  [fixedStory, variableStories[0], variableStories[2], variableStories[1]],
  [fixedStory, variableStories[1], variableStories[0], variableStories[2]],
  [fixedStory, variableStories[1], variableStories[2], variableStories[0]],
  [fixedStory, variableStories[2], variableStories[0], variableStories[1]],
  [fixedStory, variableStories[2], variableStories[1], variableStories[0]],
  [fixedStory, variableStories[0], variableStories[1], variableStories[2]],
];

const problemGroups = [
  [fixedProblem, variableProblems[0], variableProblems[1], variableProblems[2]],
  [fixedProblem, variableProblems[0], variableProblems[2], variableProblems[1]],
  [fixedProblem, variableProblems[1], variableProblems[0], variableProblems[2]],
  [fixedProblem, variableProblems[1], variableProblems[2], variableProblems[0]],
  [fixedProblem, variableProblems[2], variableProblems[0], variableProblems[1]],
  [fixedProblem, variableProblems[2], variableProblems[1], variableProblems[0]],
  [fixedProblem, variableProblems[2], variableProblems[1], variableProblems[0]],
];

function App() {
  const modes = useMemo(() => ['text', 'text-image', 'text-summary', 'text-image-summary'], []);
  const [modeIndex, setModeIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startTime, setStartTime] = useState(new Date().getTime());
  const [logData, setLogData] = useState([]);
  const [showExplain, setShowExplain] = useState(true);
  const [showProblem, setShowProblem] = useState(false);
  const [showCalculation, setShowCalculation] = useState(false);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [currentAnswers, setCurrentAnswers] = useState([]);
  const [isFinished, setIsFinished] = useState(false);
  const [groupIndex, setGroupIndex] = useState(null);
  const [totalCalculations, setTotalCalculations] = useState(0);
  const [correctCalculations, setCorrectCalculations] = useState(0);

  useEffect(() => {
    console.log("App initialized at:", new Date().getTime());
  }, []);

  const handleGroupSelection = (index) => {
    setGroupIndex(index);
  };

  const calculateTimeLimit = (text) => {
    const words = text.split(/\s+/).length;
    const minutes = words / 280;
    return minutes * 60 * 1000; // ミリ秒に変換
  };

  const handleStart = () => {
    const startClickTime = new Date().getTime();
    setLogData([...logData, { event: 'start', time: startClickTime / 1000, mode: modes[modeIndex], group: groupIndex + 1, storyIndex: currentIndex }]);
    setShowExplain(false);
    setStartTime(new Date().getTime());
  };

  const handleShowCalculation = () => {
    const calculationStartTime = new Date().getTime();
    setLogData([...logData, { event: 'calculation_start', time: calculationStartTime / 1000, mode: modes[modeIndex], group: groupIndex + 1, storyIndex: currentIndex }]);
    setShowCalculation(true);
    setStartTime(new Date().getTime());
  };

  const handleComplete = (answers) => {
    setAllQuestionsAnswered(true);
    setCurrentAnswers(answers);
  };

  const handleSubmit = () => {
    const endTime = new Date().getTime();
    const elapsedTimeInSeconds = (endTime - startTime) / 1000;
    const newLogData = [
      ...logData,
      {
        event: 'submit',
        clickTime: endTime / 1000,
        elapsedTime: elapsedTimeInSeconds,
        storyIndex: currentIndex,
        answers: currentAnswers,
        mode: modes[modeIndex],
        group: groupIndex + 1,
      },
    ];
    setLogData(newLogData);
    setStartTime(endTime);
    setShowProblem(false);
    setModeIndex((prevIndex) => (prevIndex + 1) % modes.length);

    if (currentIndex + 1 >= storyGroups[groupIndex].length) {
      setIsFinished(true);
    } else {
      setCurrentIndex((currentIndex + 1) % storyGroups[groupIndex].length);
      setShowExplain(true);
    }

    setAllQuestionsAnswered(false);
    setCurrentAnswers([]);
  };

  const handleCalculationFinish = (correctCount, totalCount) => {
    const endTime = new Date().getTime();
    const elapsedTimeInSeconds = (endTime - startTime) / 1000;
    const accuracy = (correctCount / totalCount) * 100;

    setCorrectCalculations(correctCount);
    setTotalCalculations(totalCount);

    const newLogData = [
      ...logData,
      {
        event: 'calculation_finish',
        clickTime: endTime / 1000,
        elapsedTime: elapsedTimeInSeconds,
        storyIndex: currentIndex,
        correctCalculations: correctCount,
        totalCalculations: totalCount,
        calculationAccuracy: accuracy.toFixed(2),
        group: groupIndex + 1,
      },
    ];
    setLogData(newLogData);

    setShowCalculation(false);
    setShowProblem(true);
    setStartTime(new Date().getTime());
  };

  const downloadCSV = () => {
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      'Event,Time (UNIX),Click Time (UNIX),Elapsed Time (s),Story Index,Answers,Mode,Group,Correct Calculations,Total Calculations,Calculation Accuracy\n' +
      logData.map((row) => [
        row.event || '',
        row.time || '',
        row.clickTime || '',
        row.elapsedTime || '',
        row.storyIndex !== undefined ? row.storyIndex : '',
        JSON.stringify(row.answers) || '',
        row.mode || '',
        row.group || '',
        row.correctCalculations !== undefined ? row.correctCalculations : '',
        row.totalCalculations !== undefined ? row.totalCalculations : '',
        row.calculationAccuracy !== undefined ? row.calculationAccuracy : '',
      ].join(',')).join('\n');
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(csvBlob, 'log_data.csv');
  };

  useEffect(() => {
    if (!showExplain && !showProblem && !showCalculation && currentIndex < storyGroups[groupIndex].length) {
      const currentStory = storyGroups[groupIndex][currentIndex];
      const calculatedTimeLimit = calculateTimeLimit(currentStory.content);
      const timer = setTimeout(() => {
        const nextClickTime = new Date().getTime();
        setLogData((prevLogData) => [
          ...prevLogData,
          { event: 'next', time: nextClickTime / 1000, mode: modes[modeIndex], group: groupIndex + 1, storyIndex: currentIndex }
        ]);
        setShowExplain(false);
        setShowCalculation(true);
        setStartTime(new Date().getTime());
      }, calculatedTimeLimit);
      return () => clearTimeout(timer);
    }
  }, [showExplain, showProblem, showCalculation, currentIndex, modes, modeIndex, groupIndex, logData]);

  const renderContent = () => {
    const mode = modes[modeIndex];
    const currentStory = storyGroups[groupIndex][currentIndex];
    const calculatedTimeLimit = calculateTimeLimit(currentStory.content);

    return (
      <div>
        {mode === 'text-summary' && (
          <Summary withImage={false} path_story={currentStory} />
        )}
        {mode === 'text-image-summary' && (
          <Summary withImage={true} path_story={currentStory} />
        )}
        <Show
          withImage={mode.includes('image')}
          path_story={currentStory}
          timeLimit={calculatedTimeLimit}
          onTimeUp={() => {
            const nextClickTime = new Date().getTime();
            setLogData((prevLogData) => [
              ...prevLogData,
              { event: 'next', time: nextClickTime / 1000, mode: modes[modeIndex], group: groupIndex + 1, storyIndex: currentIndex }
            ]);
            setShowExplain(false);
            setShowCalculation(true);
            setStartTime(new Date().getTime());
          }}
        />
      </div>
    );
  };

  if (isFinished) {
    return (
      <div className='bg-violet-200 p-20 min-h-screen flex flex-col justify-center items-center'>
        <FinishPage />
        <button onClick={downloadCSV} className='bg-blue-500 hover:bg-blue-700 text-white rounded cursor-pointer w-32 h-10 ml-4'>
          Download Log
        </button>
      </div>
    );
  }

  if (groupIndex === null) {
    // グループ番号選択画面を表示
    return (
      <div className='bg-violet-200 p-20 min-h-screen flex flex-col justify-center items-center'>
        <h1 className='text-2xl font-bold mb-4'>Select a Group</h1>
        <div className='flex flex-wrap justify-center'>
          {storyGroups.map((_, index) => (
            <button
              key={index}
              onClick={() => handleGroupSelection(index)}
              className='bg-blue-500 hover:bg-blue-700 text-white rounded cursor-pointer w-32 h-10 m-2'
            >
              Group {index + 1}
            </button>
          ))}
          <button
            key={6}
            onClick={() => handleGroupSelection(6)}
            className='bg-blue-500 hover:bg-blue-700 text-white rounded cursor-pointer w-32 h-10 m-2'>
            Demo
          </button>
        </div>
      </div>
    );
  }

  const currentStory = storyGroups[groupIndex][currentIndex];
  const calculatedTimeLimit = calculateTimeLimit(currentStory.content) / 60000;

  return (
    <div className='bg-violet-200 p-20 min-h-screen flex flex-col justify-between'>
      {showExplain ? (
        <div className='flex flex-col justify-center items-center flex-grow'>
          <Explain withImage={false} timeLimit={calculatedTimeLimit.toFixed(2)} />
        </div>
      ) : showProblem ? (
        <ProblemPage
          onSubmit={setCurrentAnswers}
          onComplete={handleComplete}
          questions={problemGroups[groupIndex][currentIndex].questions}
        />
      ) : showCalculation ? (
        <CalculationPage
          onTimeUp={(correctCount, totalCount) => handleCalculationFinish(correctCount, totalCount)}
          correctCalculations={correctCalculations}
          totalCalculations={totalCalculations}
        />
      ) : (
        <div className='flex flex-col justify-center flex-grow'>
          {renderContent()}
        </div>
      )}
      <div className='flex flex-col justify-center items-center justify-end mt-4'>
        {showExplain ? (
          <button onClick={handleStart} className='bg-blue-500 hover:bg-blue-700 text-white rounded cursor.pointer w-32 h-10'> Start </button>
        ) : !showProblem && !showCalculation && (
          <button onClick={handleShowCalculation} className='bg-blue-500 hover:bg-blue-700 text-white rounded cursor.pointer w-32 h-10'> Next </button>
        )}{showProblem && allQuestionsAnswered && (
          <button onClick={handleSubmit} className='bg-green-500 hover:bg-green-700 text-white rounded cursor.pointer w-32 h-10 ml-4'> Submit </button>
        )}
      </div>
    </div>
  );
}

export default App;
